import { element as el } from '../dom.js';
import { iconMarkup, type HudIcon } from '../icons.js';
import type { DamageType } from '../../../shared/game/combat-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import { logEntry } from './combat-log-roll.js';

const DAMAGE_TYPE_ICONS: Record<DamageType, HudIcon> = {
  slashing: 'gladius',
  piercing: 'dagger',
  bludgeoning: 'mailShirt',
  fire: 'fire',
  cold: 'cold',
  lightning: 'lightning',
  psychic: 'intellect',
  radiant: 'radiant',
  necrotic: 'fallen'
};
/** Lines the log shows; the server keeps 40 and sends the dice for the latest 8. */
const LOG_LINES = 8;
const LOG_KEYWORDS =new Map<string, { kind: 'good' | 'bad' | 'neutral'; icon: HudIcon }>([
  ['critically hits', { kind: 'good', icon: 'strike' }],
  ['hits', { kind: 'good', icon: 'strike' }],
  ['fires', { kind: 'good', icon: 'strike' }],
  ['resists', { kind: 'good', icon: 'shield' }],
  ['benefits', { kind: 'good', icon: 'health' }],
  ['gains', { kind: 'good', icon: 'health' }],
  ['heals', { kind: 'good', icon: 'health' }],
  ['creates an opening', { kind: 'good', icon: 'opening' }],
  ['assesses', { kind: 'good', icon: 'assess' }],
  ['anticipates danger', { kind: 'good', icon: 'anticipate' }],
  ['rolls initiative', { kind: 'neutral', icon: 'clock' }],
  ['misses', { kind: 'bad', icon: 'close' }],
  ['falls', { kind: 'bad', icon: 'fallen' }],
  ['loses', { kind: 'bad', icon: 'wounds' }],
  ['is dying', { kind: 'bad', icon: 'fallen' }],
  ['is prone', { kind: 'bad', icon: 'prone' }],
  ['is frightened', { kind: 'bad', icon: 'frightened' }],
  ['uses', { kind: 'neutral', icon: 'kit' }],
  ['stands up', { kind: 'neutral', icon: 'prone' }],
  ['wins', { kind: 'good', icon: 'party' }],
  ['drops', { kind: 'neutral', icon: 'pack' }],
]);
const STOP_WORDS = new Set(['A', 'An', 'The', 'And', 'Or', 'At', 'To', 'Of', 'For', 'With', 'By', 'On', 'In', 'From', 'It', 'Its', 'As', 'If', 'Then', 'Against', 'Is', 'Are', 'Has', 'Have', 'No', 'Not', 'Up']);

function escapeHtml(text: string) {
  return text.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] ?? char));
}
function formatCombatLogEntry(text: string): string {
  let html = escapeHtml(text);
  for (const [label, { icon }] of LOG_KEYWORDS) {
    html = html.replace(new RegExp(`\\b${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi'), match => `<span class="log-keyword log-${LOG_KEYWORDS.get(label)!.kind}">${iconMarkup(icon)}<span>${match}</span></span>`);
  }
  html = html.replace(/\b(advantage|disadvantage)\b/gi, match => `<span class="log-tag log-${match.toLowerCase()}">${iconMarkup(match.toLowerCase() === 'advantage' ? 'anticipate' : 'prone')}<span>${match}</span></span>`);
  html = html.replace(/\[((?:\d+)(?:\s*,\s*\d+)*)\]/g, (_, numbers: string) => {
    const values = numbers.split(',').map(value => value.trim());
    const parts = values.map((value, index) => index === 0
      ? `<span class="log-roll-primary">${value}</span>`
      : `<span class="log-roll-other">${value}</span>`);
    return `<span class="log-roll">[${parts.join(', ')}]</span>`;
  });
  for (const [damageType, icon] of Object.entries(DAMAGE_TYPE_ICONS) as [DamageType, HudIcon][]) {
    html = html.replace(new RegExp(`\\b${damageType}\\b`, 'gi'), match => `<span class="log-damage-type">${iconMarkup(icon)}<span>${match}</span></span>`);
  }
  html = html.replace(/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3})\b/g, match => STOP_WORDS.has(match) ? match : `<strong>${match}</strong>`);
  return html;
}
/** The page's status lines over the world: the paused banner, what the selection is fighting, and the last lines of the combat log. */
export function updateWorldStatus(state: WorldSnapshot, selected: string[]) {
  el('paused-banner').hidden = !state.paused;
  el('combat-status').hidden = !state.inCombat; el('combat-status').textContent = 'In combat';
  const own = state.heroes.find(hero => selected.includes(hero.id)), target = state.enemies.find(enemy => enemy.id === own?.combat.targetId);
  if (own && target && !target.health.dead) el('combat-status').textContent += ` · ${own.route.length ? 'Approaching' : own.combat.actions ? 'Attacking' : 'Waiting for actions:'} ${target.name}`;
  renderCombatLog(el('combat-log'), state.combatLog.slice(-LOG_LINES));
}
/** Lines are kept between snapshots, not rebuilt: a line under the pointer keeps its hover. */
const shownLines = new WeakMap<HTMLElement, Map<string, HTMLElement>>();
function renderCombatLog(log: HTMLElement, entries: WorldSnapshot['combatLog']) {
  const lines = shownLines.get(log) ?? new Map<string, HTMLElement>();
  shownLines.set(log, lines);
  const wanted = new Set(entries.map(entry => `${entry.id}:${entry.text}`));
  for (const [key, line] of lines) if (!wanted.has(key)) { line.remove(); lines.delete(key); }
  let added = false;
  for (const entry of entries) {
    const key = `${entry.id}:${entry.text}`;
    if (lines.has(key)) continue;
    const line = logEntry(entry.roll, formatCombatLogEntry(entry.text));
    lines.set(key, line); log.append(line); added = true;
  }
  if (added) log.scrollTop = log.scrollHeight;
}
